import React, { useRef, useState, useEffect } from "react";
import HTMLFlipBook from "react-pageflip";
import { Helmet } from "react-helmet-async";
import Footer from "./Footer";
import styles from "./Portfolio.module.css";

import slide1 from "../portfolio-img/Slide1.jpg";
import slide2 from "../portfolio-img/Slide2.jpg";
import slide3 from "../portfolio-img/Slide3.jpg";
import slide4 from "../portfolio-img/Slide4.jpg";
import slide5 from "../portfolio-img/Slide5.jpg";
import slide6 from "../portfolio-img/Slide6.jpg";
import mergeImage from "../portfolio-img/Merged.png";

const Portfolio = () => {
  const flipBookRef = useRef(null);
  const brochureContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isFlipBookOpen, setIsFlipBookOpen] = useState(false);
  const [isOnLastPageState, setIsOnLastPageState] = useState(false);
  const slides = [slide1, slide2, slide3, slide4, slide5, slide6];
  const mobileSlides = [slide1, slide2, slide3, mergeImage, slide6]; // Replace slide4&5 with merge image

  // Handle flipbook state changes
  const handleFlip = (e) => {
    // Center the book when on first page (0) OR last page (5)
    // For 6 slides: indices 0,1,2,3,4,5 - so last page is index 5
    const isOnFirstPage = e.data === 0;
    const isOnLastPage = e.data === slides.length - 1; // slides.length - 1 = 5
    
    // Track if we're on the last page for styling
    setIsOnLastPageState(isOnLastPage);
    
    // Book is "closed" (centered) only on first page OR last page
    setIsFlipBookOpen(!isOnFirstPage && !isOnLastPage);
  };

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate dimensions to fit viewport perfectly - ensure full visibility
  // Desktop/Laptop: Original size, Mobile/Tablet: Adjusted for better fit
  const isSmallDevice = isMobile || isTablet;
  
  const availableHeight = isMobile ? viewportHeight * 0.6 : (isTablet ? viewportHeight * 0.4 : viewportHeight * 0.8);
  const availableWidth = isMobile ? viewportWidth * 0.55 : (isTablet ? viewportWidth * 0.75 : viewportWidth * 0.8);

  // Use the constraining dimension - adjust aspect ratio for mobile only
  const aspectRatio = isMobile ? 0.9 : (isTablet ? 0.9 : 1.45);
  const pageHeight = Math.min(availableHeight, availableWidth * aspectRatio);
  const pageWidth = pageHeight / aspectRatio;

  // Zoom levels: 1x, 1.25x, 1.5x
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 1.5));
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - 0.25, 1);
    setZoomLevel(newZoom);
    // Reset drag offset when zooming out to default
    if (newZoom === 1) {
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
  };

  // Drag handlers - only active when zoomed
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = React.useCallback((e) => {
    if (isDragging && zoomLevel > 1) {
      setDragOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, zoomLevel, dragStart.x, dragStart.y]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - dragOffset.x,
        y: touch.clientY - dragOffset.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const touch = e.touches[0];
      setDragOffset({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className={styles.portfolioWrapper}>
      <Helmet>
        <title>Portfolio - Ace Keystone Consultants</title>
        <meta name="description" content="Explore our portfolio showcasing our projects and achievements at Ace Keystone Consultants." />
      </Helmet>

      {/* Banner Section */}
      <div className={styles.bannerSection}>
        <div className={styles.bannerContent}>
          <h1 className={`${styles.bannerTitle} ${styles.fadeUp}`}>
            BROCHURE & OUR CUSTOMER JOURNEY
          </h1>
          <p className={`${styles.bannerDescription} ${styles.fadeUpDelayed}`}>
            Explore our innovation journey, product portfolio, and deep-tech solutions through our featured projects and technology showcases.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection}>
        {/* Desktop/Laptop: Show brochure format */}
        {!isSmallDevice && (
          <div
            ref={brochureContainerRef}
            className={`${styles.flipbookContainer} ${isFlipBookOpen ? styles.opened : ''} ${isOnLastPageState ? styles.lastPage : ''} ${zoomLevel > 1 ? styles.zoomed : ''}`}
            style={{
              transform: `scale(${zoomLevel}) translate(${dragOffset.x / zoomLevel}px, ${dragOffset.y / zoomLevel}px)`,
              transformOrigin: isFlipBookOpen ? 'right center' : 'center center',
              transition: isDragging ? 'none' : 'transform 0.3s ease, transform-origin 800ms ease-in-out',
              cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              '--page-width': `${pageWidth}px`,
              '--page-height': `${pageHeight}px`
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <HTMLFlipBook
              ref={flipBookRef}
              width={pageWidth}
              height={pageHeight}
              size="fixed"
              showCover={true}
              startPage={0}
              usePortrait={false}
              onFlip={handleFlip} // Track when book is opened
              className={styles.flipbook}
              flippingTime={800}
              mobileScrollSupport={true}
              startZIndex={1}
              autoSize={false}
              maxShadowOpacity={0.5}
              showPageCorners={true}
              disableFlipByClick={false}
            >
              {slides.map((slide, idx) => (
                <div key={idx} className={styles.page}>
                  <img src={slide} alt={`Slide ${idx + 1}`} className={styles.pageImg} />
                </div>
              ))}
            </HTMLFlipBook>
          </div>
        )}

        {/* Mobile/Tablet: Show scrollable images with zoom */}
        {isSmallDevice && (
          <div
            className={styles.scrollableContainer}
            style={{
              transform: `scale(${zoomLevel}) translate(${dragOffset.x / zoomLevel}px, ${dragOffset.y / zoomLevel}px)`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.3s ease',
              cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {mobileSlides.map((slide, idx) => (
              <div key={idx} className={styles.scrollableImage}>
                <img src={slide} alt={`Slide ${idx + 1}`} className={styles.scrollImg} />
              </div>
            ))}
          </div>
        )}

        {/* Controls Section - Zoom */}
        <div className={styles.controlsSection}>
          {/* Zoom Controls - Available on all devices */}
          <div className={styles.zoomControls}>
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel === 1}
              className={styles.zoomBtn}
              aria-label="Zoom out"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="8" y1="11" x2="14" y2="11"></line>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>

            <span className={styles.zoomLevel}>{Math.round(zoomLevel * 100)}%</span>

            <button
              onClick={handleZoomIn}
              disabled={zoomLevel === 1.5}
              className={styles.zoomBtn}
              aria-label="Zoom in"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>

            {zoomLevel !== 1 && (
              <button
                onClick={handleResetZoom}
                className={styles.resetZoomBtn}
                aria-label="Reset zoom"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
};

export default Portfolio;
